import { type ClientId } from './client/bridge-api-client-types';
import type { RovoContentBridgeSetupHandler } from './create-bridge-client-types';
import { type RovoContentBridgeAPIShape } from './rovo-bridge-api-interface';
import { getBrowserBridgeAPI } from './rovo-bridge-api-singleton';

/**
 * Create a standalone bridge API instance with an opinionated approach for setting up commands.
 *
 * @description A constrained API for creating a bridge API instance, to ensure command handlers are registered
 * prior to any messages being received by this bridge API client. We want to avoid the scenario where a client
 * indicates itself as being ready *before* it registers handlers for specific commands.
 *
 * @param id unique identifier for the bridge API client
 * @param setupHandlers a callback inside of which a client can safely setup command handlers before receiving
 * any messages from a remote client to handle the message which was sent to it
 * @param api a bridge API instance for use with this specific bridge API client
 *
 * @returns a bridge API instance for use with this client
 * @deprecated - use {@link createBridgeClient} instead
 */
export const createRovoBridgeAPIClient = (
	id: ClientId,
	setupHandlers?: RovoContentBridgeSetupHandler,
	api: RovoContentBridgeAPIShape = getBrowserBridgeAPI(),
) => {
	// setup command handlers before indicating this client is ready
	setupHandlers?.((command, handler) => api.on(id, command, handler));

	// indicate as ready
	api.ready(id);

	// start using the API
	return api;
};

// See [Barrel File Removal FAQ](https://hello.atlassian.net/wiki/x/KJT2aAE)
/* eslint-disable no-barrel-files/no-barrel-files */
export { type RovoContentBridgeAPIShape } from './rovo-bridge-api-interface';
export { type RovoContentBridgeSetupHandler } from './create-bridge-client-types';
